import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { Box, Button, IconButton, Stack, Tab, Tabs } from "@mui/material";
import { useState, type DragEvent, type FC, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { UUID } from "../../../utils/uuid";
import { AssetCount } from "../asset-count";
import { AssetGrid } from "../asset-grid";
import type { ContentLibraryState } from "../reducer/reducer";
import type { Asset } from "../server/digital-asset-service";
import type { Folder } from "../server/folder-service";
import { ROOT_ID } from "../utils";

type ContentLibraryAssetsProps = {
  filteredData: Asset[];
  allFilteredData: Asset[];
  state: ContentLibraryState;
  slots: {
    breadcrumb: ReactElement;
    filter: ReactElement;
    contentTypeChips: ReactElement;
    searchField: ReactElement;
    tagChips: ReactElement;
  };
  designer?: string | null;
  onSelectAsset: (asset: Asset) => void;
  onCreateFolder: () => void;
  filteredFolders: Folder[];
  allFilteredFolders: Folder[];
  onArchiveFolder: () => void;
  onMoveFolder: () => void;
  allFolders?: Folder[];
  onClickFolder: (folderId: UUID) => void;
  onAssetDragStart?: (asset: Asset, event: DragEvent) => void;
  onFolderDragStart?: (folder: Folder, event: DragEvent) => void;
  onFolderDragOver?: (event: DragEvent) => void;
  onFolderDrop?: (folderId: Folder["id"], event: DragEvent) => void;
};

export const ContentLibraryAssets: FC<ContentLibraryAssetsProps> = ({
  filteredData,
  allFilteredData,
  state,
  slots,
  designer,
  onSelectAsset,
  onCreateFolder,
  filteredFolders,
  allFilteredFolders,
  onArchiveFolder,
  onMoveFolder,
  allFolders,
  onClickFolder,
  onAssetDragStart,
  onFolderDragStart,
  onFolderDragOver,
  onFolderDrop,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"this-folder" | "all-folders">("this-folder");

  const inRootFolder = !state.selectedTreeItemId || state.selectedTreeItemId === ROOT_ID;
  const hasSearch = Boolean(state.searchText.trim());
  const showAllFoldersResults = hasSearch && tab === "all-folders";
  const assetsToShow = showAllFoldersResults ? allFilteredData : filteredData;
  const foldersToShow = showAllFoldersResults ? allFilteredFolders : filteredFolders;
  return (
    <Stack>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {slots.breadcrumb}
        <Stack direction="row" alignItems="center" gap={1}>
          {!inRootFolder && (
            <Button
              variant="text"
              color="primary"
              data-analytics-id="content-library-move-folder"
              onClick={onMoveFolder}
            >
              {t("Move folder")}
            </Button>
          )}
          <Button
            variant="text"
            color="primary"
            data-analytics-id="content-library-create-sub-folder"
            data-testid="content-library-create-sub-folder"
            onClick={onCreateFolder}
          >
            {t("Create sub-folder")}
          </Button>
          {!inRootFolder && (
            <IconButton
              data-testid="archive-folder"
              data-analytics-id="content-library-archive-folder"
              aria-label="Archive folder"
              onClick={onArchiveFolder}
            >
              <DeleteOutline color="error" sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>
      </Box>
      <Stack direction="row" justifyContent="space-between" pb={2}>
        <Box flexGrow={1} mr={2}>
          {slots.searchField}
        </Box>
        {slots.filter}
      </Stack>
      {hasSearch && (
        <Tabs
          value={tab}
          onChange={(_, nextTab) => setTab(nextTab as "this-folder" | "all-folders")}
          sx={{ mb: 2 }}
        >
          <Tab
            label={t(`Results in this folder (${filteredData.length + filteredFolders.length})`)}
            value="this-folder"
          />
          <Tab
            label={t(
              `Results in all folders (${allFilteredData.length + allFilteredFolders.length})`,
            )}
            value="all-folders"
          />
        </Tabs>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <AssetCount assets={assetsToShow} folders={foldersToShow} searchText={state.searchText} />
      </Stack>
      <Stack gap={1} sx={{ mb: 2 }}>
        {slots.contentTypeChips}
        {slots.tagChips}
      </Stack>
      <AssetGrid
        allFolders={allFolders}
        assets={assetsToShow}
        onClick={onSelectAsset}
        designer={designer}
        folders={foldersToShow}
        showFolderNames={showAllFoldersResults}
        showFolders={state.appliedTags.length === 0 && state.appliedContentTypes.length === 0}
        onClickFolder={onClickFolder}
        onAssetDragStart={onAssetDragStart}
        onFolderDragStart={onFolderDragStart}
        onFolderDragOver={onFolderDragOver}
        onFolderDrop={onFolderDrop}
      />
    </Stack>
  );
};
